import dayjs from 'dayjs'

import { FutureRateFirstInstallment } from '../../../utils/Constants'
import { getAudurApiClient } from '../../../utils/Utils'
import {
	GetMortgageSettingsResponse,
	LoanCalculationCalculatedData,
	LoanCalculationInputs,
	LoanCalculationRequest,
	LoanCalculationResponse,
	LoanTypeEnumCamelCase,
} from '../newCalculator.models'

const useCalculatorRequests = () => {
	const client = getAudurApiClient()

	const calculateLoanData = async (
		calculatorValues: LoanCalculationInputs,
		defaultOptions: GetMortgageSettingsResponse | null,
		loanType: LoanTypeEnumCamelCase,
		reductionDuration = 36 // in months,
	): Promise<LoanCalculationCalculatedData | null> => {
		try {
			const values = { ...calculatorValues }

			if (values.fixedInterestPercentage === undefined || values.fixedInterestPercentage === null) {
				const interestRates = defaultOptions?.interestRates ?? []
				values.fixedInterestPercentage =
					(interestRates.find((x) => x.productType === (values.selectedPlan ?? loanType))?.interestRate ??
						interestRates[0]?.interestRate ??
						0.089) * 100
			}

			const getInterestRates = (
				caculatorValues: LoanCalculationInputs,
				defaultOptions: GetMortgageSettingsResponse | null,
				loanType: LoanTypeEnumCamelCase
			) => {
				const selectedInterestRateOption = defaultOptions?.interestRates.find(
					(x) => x.productType === (caculatorValues.selectedPlan ?? loanType)
				)

				if (!selectedInterestRateOption) {
					return undefined
				}

				if (caculatorValues.useCustomInterestRate && caculatorValues.fixedInterestPercentage != null) {
					const customRate = caculatorValues.fixedInterestPercentage
					if (caculatorValues.interestRateOptionFixed) {
						return [{ interestRatePercentage: customRate, validFromInstallment: 1 }]
					}
					return [
						{ interestRatePercentage: customRate, validFromInstallment: 1 },
						{ interestRatePercentage: customRate, validFromInstallment: FutureRateFirstInstallment },
					]
				}

				if (!caculatorValues.propertyMarketValueAmount || caculatorValues.propertyMarketValueAmount <= 0) {
					return undefined
				}

				const ltv = (caculatorValues.loanAmount / caculatorValues.propertyMarketValueAmount) * 100
				const { ltvBaseThreshold, ltvMaxThreshold, ltvFutureSurchargePerPoint, ltvSurchargePerPoint } =
					selectedInterestRateOption

				const computeSurcharge = (surchargePerPoint: number) => {
					if (ltv < ltvBaseThreshold) {
						return 0
					}
					if (ltv < ltvMaxThreshold) {
						return (ltv - ltvBaseThreshold) * surchargePerPoint
					}
					return (ltvMaxThreshold - ltvBaseThreshold) * surchargePerPoint
				}

				const surchargePercentage = computeSurcharge(ltvSurchargePerPoint)
				const futureSurchargePercentage = computeSurcharge(ltvFutureSurchargePerPoint)

				const interestRate =
					selectedInterestRateOption.centralBankInterestRate +
					selectedInterestRateOption.additionalFee +
					surchargePercentage
				const futureInterestRate =
					selectedInterestRateOption.centralBankInterestRate +
					selectedInterestRateOption.futureAdditionalFee +
					futureSurchargePercentage

				// for fixed interest rate option, we only send interest rate from the input
				if (caculatorValues.interestRateOptionFixed) {
					return [
						{
							interestRatePercentage: interestRate * 100,
							validFromInstallment: 1,
						},
					]
				}

				// for variable interest rate option, we send two interest rates: base rate and future rate (after 3 years)
				return [
					{
						interestRatePercentage: interestRate * 100,
						validFromInstallment: 1,
					},
					{
						interestRatePercentage: futureInterestRate * 100,
						validFromInstallment: FutureRateFirstInstallment,
					},
				]
			}

			const mapRequestData: LoanCalculationRequest = {
				loanAmount: calculatorValues.loanAmount,
				principalPaymentType: calculatorValues.principalPaymentType,
				numberOfPaymentsMonths: calculatorValues.numberOfPaymentsMonths * 12,
				extraMonthlyPaymentToPrincipal: calculatorValues.extraMonthlyPaymentToPrincipal,
				propertyMarketValueAmount: calculatorValues.propertyMarketValueAmount,
				annualHousingPriceIncreasePercentage: calculatorValues.annualHousingPriceIncreasePercentage,
				paymentReduction:
					calculatorValues.optionBAmount > 0
						? {
								amount: calculatorValues.optionBAmount ?? 1,
								lengthInMonths: calculatorValues.optionBLengthInYears * 12,
							}
						: undefined,
				useRealValues: false,
				interestRates: getInterestRates(calculatorValues, defaultOptions, loanType),
			}

			const { data } = await client.axiosInstance.post<LoanCalculationResponse>(
				`/mortgages/calculation`,
				mapRequestData
			)

			if (!data) {
				return null
			}

			const latestInstallement = data?.installments?.slice(-1)[0] ?? 0
			const estimatedEquityAfterXYears =
				data?.loanLengthInMonths > reductionDuration
					? (data?.installments.find((x) => x.number === reductionDuration)?.propertyEquity ?? 0)
					: latestInstallement.propertyEquity

			const mappedResult: LoanCalculationCalculatedData = {
				indexationOnInterest: -9999, // Verðbætur v/vaxta - not sure what this is
				priceAdjustments: -9999,

				totalRepayment: data.totalRepayment,
				totalPrincipal: data.totalPrincipal,
				disbursementFee: data.disbursementFee,
				disbursement: data.disbursement,
				nextPayment: data.nextPayment,
				avgPayment: data.avgPayment,
				totalPaymentFee: data.totalPaymentFee,
				annualPercentageRate: data.annualPercentageRate,
				estimatedEquityAfterXYears,
				reductionPaymentAfterXYears:
					latestInstallement.number > reductionDuration
						? (data?.installments.find((x) => x.number === reductionDuration)?.total ?? 0)
						: latestInstallement.total,
				interestRate: (data.interestRates?.[0]?.interestRate ?? data.interestRate) * 100,
				settlementCosts: data.settlementCosts,
				paymentReductionMaxAmount: data.paymentReductionMaxAmount,
				paymentReductionMinAmount: data.paymentReductionMinAmount,

				installments: data.installments,
				graphData: data?.installments
					.filter((x) => x.number === 1 || x.number % 12 === 0)
					.map((x) => ({
						year: dayjs(x.dueDate).year(),
						value: x.propertyEquity,
					})),
				monthlyInstallementChartData: data?.installments
					.filter((x) => x.number === 1 || x.number % 12 === 0)
					.map((x) => ({
						year: dayjs(x.dueDate).year(),
						value: x.total,
					})),
				principalReductionChartData: data?.installments
					.filter((x) => x.number === 1 || x.number % 12 === 0)
					.map((x) => ({
						year: dayjs(x.dueDate).year(),
						value: x.remainingPrincipal,
					})),
			}

			return mappedResult
		} catch (error) {
			console.error('Error calculating loan data:', error)
		}

		return null
	}

	const getCalculatorOptions = async () => {
		try {
			const response = await client.axiosInstance.get<GetMortgageSettingsResponse>(`/mortgages/settings`)

			return response.data
		} catch (error) {
			console.error('Error fetching calculator options:', error)
		}

		return null
	}

	return {
		calculateLoanData,
		getCalculatorOptions,
	}
}

export default useCalculatorRequests
